import classNames from 'classnames';
import Checkbox from 'components/atoms/Checkbox';
import Label from 'components/foundations/Label';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { withLocalize } from 'react-localize-redux';
import { Virtuoso } from 'react-virtuoso';
import IconMobile from 'resources/svg/icon_mobile.svg';
import AutoComplete from '../../components/molecules/AutoComplete';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import { ItemSchema } from '../types';
import { translationPrefix } from '../utils';
import css from './SearchableMultiSelect.css';
import SelectAll from './SelectAll';

const ItemType = {
    PARENT: 'parent',
    SUB_ITEMS: 'subItems',
    REGULAR: 'regular',
};

const SearchableMultiSelect = ({
    allItems,
    filteredItems,
    onChange,
    className,
    selectAll,
    translate,
    itemType = '',
    sorted = true,
    searchPlaceholder = '',
    showIcon = true,
}) => {
    const [expandedParentItems, setExpandedParentItems] = useState({});
    const [searchFilter, setSearchFilter] = useState('');

    const disabledCount = allItems.filter(item => item.disabled).length;
    const selectedCount = allItems.filter(item => item.selected).length;
    const totalCount = allItems.length;
    const isSelectAll = totalCount - disabledCount === selectedCount;

    const toggleExpandedItem = id => setExpandedParentItems(prev => ({ ...prev, [id]: !prev[id] }));

    const getItemLabel = item => (item.displayName || item.name || '').toLowerCase();

    // Filter items based on search term
    const searchTerm = (searchFilter || '').toLowerCase().trim();
    const itemsToDisplay = useMemo(() => {
        if (!searchTerm) return filteredItems;

        return filteredItems.filter(item => {
            const nameMatch = getItemLabel(item).includes(searchTerm);
            const subItemsMatch = item.subItems?.some(subItem => getItemLabel(subItem).includes(searchTerm));
            return nameMatch || subItemsMatch;
        });
    }, [filteredItems, searchTerm]);

    const getPriority = useCallback(
        item => {
            if (!sorted || item.selected) return 0;
            if (item.subItems?.some(subItem => subItem.selected)) return 1;
            return 2;
        },
        [sorted]
    );

    const rows = useMemo(() => {
        const result = [];

        itemsToDisplay
            .sort((a, b) => getPriority(a) - getPriority(b))
            .forEach(item => {
                if (item.subItems?.length > 0) {
                    result.push({ type: ItemType.PARENT, item });
                    if (expandedParentItems[item.id]) {
                        result.push({ type: ItemType.SUB_ITEMS, item, subItems: item.subItems });
                    }
                } else {
                    result.push({ type: ItemType.REGULAR, item });
                }
            });

        return result;
    }, [getPriority, itemsToDisplay, expandedParentItems]);

    const renderItem = ({
        item: { Icon: IconComponent = IconMobile, iconProps = {}, name, selected, id, displayName, extra, disabled },
        onClick,
        onToggle = onChange,
        isSubItem = false,
        className: itemClassName = '',
        children,
        indeterminate = false,
        info = '',
    }) => (
        <div key={id} className={classNames(css.item, itemClassName, { [css.disabled]: disabled })} onClick={onClick}>
            <Checkbox
                className={css.checkbox}
                checked={selected}
                indeterminate={indeterminate}
                size="small"
                onChange={newState => onToggle(id, newState, isSubItem)}
                disabled={disabled}
            />
            {children}
            <div className={css.itemContent}>
                {showIcon && <IconComponent className={css.logo} {...iconProps} />}
                <div className={classNames(css.name, { [css.withInfo]: info })}>{displayName || name}</div>
                {info && <Label type="p" text={info} />}
                {extra && <div className={css.extra}>{extra}</div>}
            </div>
        </div>
    );

    const renderParentItem = item =>
        renderItem({
            item,
            onClick: () => toggleExpandedItem(item.id),
            className: css.parentItem,
            children: (
                <div className={css.toggleSection}>
                    <div className={css.separator} />
                    <Icon
                        name={expandedParentItems[item.id] ? 'chevron-down' : 'chevron-right'}
                        size={IconSizes.X_SMALL}
                    />
                </div>
            ),
            info: translate('STATIC.PAGES.TEAM_MEMBERS.MANAGE_PERMISSIONS_PAGE.ITEM_SELECTED', {
                selected: item.subItems.filter(subItem => subItem.selected).length,
                total: item.subItems.length,
                itemType,
            }),
            indeterminate: !item.selected && item.subItems.some(subItem => subItem.selected),
        });

    const renderSubItem = (item, parentItem) => {
        const disabled = parentItem.disabled || item.disabled;
        return renderItem({
            item: { ...item, disabled },
            onClick: () => !disabled && onChange(item.id, !item.selected, true),
            className: css.subItem,
            isSubItem: true,
        });
    };

    const renderRow = row => {
        switch (row.type) {
            case ItemType.PARENT:
                return renderParentItem(row.item);
            case ItemType.SUB_ITEMS:
                return (
                    <div className={css.subItems}>{row.subItems.map(subItem => renderSubItem(subItem, row.item))}</div>
                );
            default:
                return renderItem({
                    item: row.item,
                    onClick: () => !row.item.disabled && onChange(row.item.id, !row.item.selected),
                });
        }
    };

    return (
        <div className={classNames(css.wrapper, className)}>
            <AutoComplete
                placeholder={searchPlaceholder || translate(`${translationPrefix}.PLACEHOLDER.APP_SEARCH`)}
                selectOptions={{
                    options: [],
                    noOptionsMessage: () => null,
                    isSearchable: true,
                    inputValue: searchFilter,
                }}
                onInputChange={setSearchFilter}
                controlledValue={searchFilter}
            />
            <SelectAll allItems={allItems} onChange={() => selectAll(!isSelectAll)} />
            <div className={css.scrollableContent}>
                <Virtuoso
                    data={rows}
                    computeItemKey={(_, row) => `${row.type}-${row.item.id}`}
                    itemContent={(_, row) => renderRow(row)}
                />
            </div>
        </div>
    );
};

SearchableMultiSelect.propTypes = {
    allItems: PropTypes.arrayOf(ItemSchema).isRequired,
    filteredItems: PropTypes.arrayOf(ItemSchema).isRequired,
    onChange: PropTypes.func.isRequired,
    selectAll: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
    itemType: PropTypes.string,
    className: PropTypes.string,
    sorted: PropTypes.bool,
    searchPlaceholder: PropTypes.string,
    showIcon: PropTypes.bool,
};

export default withLocalize(SearchableMultiSelect);
